import {
    getBuComparisonLabel,
    mapPsOhiEmployeeExperienceText,
} from '../Diagnose/IndExperienceBoard/indExperiencePublicSector';
import { getBuildReportPayloadAiOra, getDefaultBuildYourReportPayload } from './constants';

export const getPsOhiExLabel = (isPublicSectorOhi, reportType, text, additionalPracticesLabel) => {
    if (!isPsOhiOhiReport(isPublicSectorOhi, reportType)) return text;
    return mapPsOhiEmployeeExperienceText(text, true, additionalPracticesLabel);
};

const mapPsOhiBuildReportOptionLabels = (options, isPublicSectorOhi, additionalPracticesLabel) =>
    (options || []).map(option => ({
        ...option,
        label: mapPsOhiEmployeeExperienceText(option.label, isPublicSectorOhi, additionalPracticesLabel),
        children: option.children?.map(child => ({
            ...child,
            label: mapPsOhiEmployeeExperienceText(child.label, isPublicSectorOhi, additionalPracticesLabel),
            children: child.children?.map(grandchild => ({
                ...grandchild,
                label: mapPsOhiEmployeeExperienceText(grandchild.label, isPublicSectorOhi, additionalPracticesLabel),
            })),
        })),
    }));

export const mapPsOhiBuildReportOptions = (options, isPublicSectorOhi, reportType, additionalPracticesLabel) => {
    if (!isPsOhiOhiReport(isPublicSectorOhi, reportType)) return options;
    return mapPsOhiBuildReportOptionLabels(options, true, additionalPracticesLabel);
};

export const isPsOhiEnterpriseReport = (isPublicSectorOhi, reportType) => isPublicSectorOhi && `${reportType}` === '0';

export const isPsOhiBuReport = (isPublicSectorOhi, reportType) => isPublicSectorOhi && `${reportType}` === '1';

export const isPsOhiOhiReport = (isPublicSectorOhi, reportType) =>
    isPublicSectorOhi && (`${reportType}` === '0' || `${reportType}` === '1');

export const AI_ORA_REPORT_TYPE = '10';

/** Report types > 3 skip Build Your Report, except AI-ORA (type 10) which requires that step. */
export const shouldSkipBuildReportForReportType = reportType => {
    const type = `${reportType}`.trim();
    if (!type || type === AI_ORA_REPORT_TYPE) return false;
    const reportTypeNum = parseInt(type, 10);
    return !Number.isNaN(reportTypeNum) && reportTypeNum > 3;
};

/**
 * Hide Build Your Report for public-sector OHI Enterprise (0), Unit (1), and AI-ORA (10) reports.
 * Also hides on initial Choose Report load before ReportType is synced (empty type).
 */
export const hidePsOhiBuildReportSection = (isPublicSectorOhi, reportType) => {
    if (!isPublicSectorOhi) return false;
    const type = reportType == null || `${reportType}`.trim() === '' ? '' : `${reportType}`.trim();
    return type === '' || type === '0' || type === '1' || type === AI_ORA_REPORT_TYPE;
};

/** Default build_your_report when Build Your Report is hidden for public-sector OHI flows. */
export const getPsOhiHiddenBuildReportPayload = ({
    reportType,
    aiOraModuleType = '',
    isValuesModule,
    isEmployeeExperience,
    isPulseSurvey,
    enablePracticeRanking,
    enablePulseRanking,
    compYrApplyExBm,
    settingsFetched,
    empExpResurveySettings,
    surveyReportType,
    applyEmployeeExpBenchmark,
}) => {
    if (`${reportType}`.trim() === AI_ORA_REPORT_TYPE) {
        return { ...getBuildReportPayloadAiOra(aiOraModuleType) };
    }
    return getDefaultBuildYourReportPayload({
        reportType,
        isValuesModule,
        isEmployeeExperience,
        isPulseSurvey,
        enablePracticeRanking,
        enablePulseRanking,
        compYrApplyExBm,
        settingsFetched,
        empExpResurveySettings,
        surveyReportType,
        applyEmployeeExpBenchmark,
    });
};

export const getOhiBuReportLabel = (isPublicSectorOhi, ohiText) =>
    `${ohiText} ${getBuComparisonLabel(isPublicSectorOhi, 'BU Report', 'Unit Report')}`;

export const getPsOhiBuDemographicLabels = (staticText, isPublicSectorOhi) => {
    if (!isPublicSectorOhi) {
        return {};
    }
    const {
        BU_GROUPS = '',
        COMPARISION_BU_TEXT = '',
        COMPARISON_BU_PLACEHOLDER = '',
        BU_BUSINESS_TEXT = '',
        UNIT_COMPARISON = 'Unit Comparison',
    } = staticText?.SITE_TEXT || {};
    const unitComparisonPlaceholder = COMPARISON_BU_PLACEHOLDER.replace(/\bBU\b/g, 'Unit');

    return {
        buGroups: getBuComparisonLabel(true, BU_GROUPS, `${UNIT_COMPARISON} Groups`),
        comparisionBuText: getBuComparisonLabel(true, COMPARISION_BU_TEXT, UNIT_COMPARISON),
        comparisonBuPlaceholder: getBuComparisonLabel(true, COMPARISON_BU_PLACEHOLDER, unitComparisonPlaceholder),
        buBusinessText: BU_BUSINESS_TEXT.replace(/business unit/gi, 'unit'),
    };
};

export const mapPublicSectorReportOption = (option, isPublicSectorOhi, ohiText) => {
    if (!isPublicSectorOhi || option.key !== '1') return option;
    const label =
        option.label === 'BU Report'
            ? getBuComparisonLabel(true, 'BU Report', 'Unit Report')
            : getOhiBuReportLabel(true, ohiText);
    return { ...option, label };
};

export const mapPublicSectorReportOptions = (options, isPublicSectorOhi, ohiText) =>
    (options || []).map(option => mapPublicSectorReportOption(option, isPublicSectorOhi, ohiText));

export const getPublicSectorReportType = (reportType, isPublicSectorOhi, ohiText) => {
    if (!isPublicSectorOhi || !reportType?.length) return reportType;
    const updated = [...reportType];
    updated[1] = getOhiBuReportLabel(true, ohiText);
    return updated;
};

export const getReportDynamicTextInfo = (dynamicTextInfo, { ohi_text, is_public_sector_ohi = false }) => {
    const reportTextInfo = dynamicTextInfo({ ohi_text });
    if (!is_public_sector_ohi) return reportTextInfo;

    return {
        ...reportTextInfo,
        REPORT_TYPE: getPublicSectorReportType(reportTextInfo.REPORT_TYPE, true, ohi_text),
        CHOOSE_REPORT_OPTIONS: mapPublicSectorReportOptions(reportTextInfo.CHOOSE_REPORT_OPTIONS, true, ohi_text),
        ING_REPORT_OPTIONS: mapPublicSectorReportOptions(reportTextInfo.ING_REPORT_OPTIONS, true, ohi_text),
        THOMSON_REPORT_OPTIONS: mapPublicSectorReportOptions(reportTextInfo.THOMSON_REPORT_OPTIONS, true, ohi_text),
    };
};
